import { Box, Chip, Collapse, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AgChartOptions } from 'ag-charts-enterprise';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { truckModeEnum } from '../../../../constants';
import { getMaterialStateColor } from '../../../../constants/colors';
import { isTruckContextEnabled } from '../../../../constants/helpers/truck';
import { getTruckModeLabel } from '../../../../constants/labels';
import { useSitesTrucksSupervisionQuery } from '../../../../hooks/queries/useTrucksQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { DailyTruckRide } from '../../../../services/apiSLTP/dailyReports/dailyTrucksRides';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { formatTime } from '../../../../utils/dates';
import { formatNumber } from '../../../../utils/numbers';
import GenericAgChart from '../../../charts/GenericAgChart';
import LicensePlate from '../../../domain/LicensePlate';
import ScrollPageContainer from '../../../layout/ScrollPageContainer';
import Icon from '../../../ui/Icon';
import SectionCard from '../../../ui/SectionCard';

interface TrucksLiveSupervisionProps {
    date: Date;
    onlyMySites?: boolean;
}

interface StatDisplayProps {
    count?: number;
    tonnage?: number;
    label: string;
    color?: string;
    variant?: 'h4' | 'h5' | 'h6';
    locale: string;
    minWidth?: number;
}

const StatDisplay: React.FC<StatDisplayProps> = ({ count, tonnage, label, color, variant = 'h6', locale, minWidth = 80 }) => {
    const hasData = !!count || !!tonnage;

    if (!hasData) return null;

    return (
        <Box display="flex" justifyContent="flex-end" alignItems="baseline" gap={0.5} minWidth={minWidth} sx={{ flexShrink: 0 }}>
            {count !== undefined && (
                <Typography variant={variant} color={color || 'primary'} fontWeight="bold">
                    {count}
                </Typography>
            )}
            {tonnage !== undefined && (
                <Typography variant={variant} sx={{ color, fontWeight: 'bold' }}>
                    {formatNumber(tonnage, locale, 2)} t
                </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {label}
            </Typography>
        </Box>
    );
};

interface RideBadgeProps {
    time?: Date;
    departureTonnage?: number;
    arrivalTonnage?: number;
}

const RideBadge: React.FC<RideBadgeProps> = ({ time, departureTonnage, arrivalTonnage }) => {

    return (
        <Paper
            sx={{
                minWidth: 50,
                height: 28,
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: 0.5,
            }}
            elevation={2}
        >
            <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem', lineHeight: 1 }}>
                {formatTime(time)}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                {departureTonnage && (
                    <Typography variant="caption" sx={{ color: '#00b8c2', fontWeight: 'bold', fontSize: '0.6rem', lineHeight: 1 }}>
                        {departureTonnage}t
                    </Typography>
                )}
                {arrivalTonnage && (
                    <Typography variant="caption" sx={{ color: 'lightgreen', fontWeight: 'bold', fontSize: '0.6rem', lineHeight: 1 }}>
                        {arrivalTonnage}t
                    </Typography>
                )}
            </Box>
        </Paper>
    );
};

interface TruckCardProps {
    truck: any;
    siteName: string;
    locale: string;
}

const TruckCard: React.FC<TruckCardProps> = ({ truck, siteName, locale }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [expanded, setExpanded] = useState(false);

    const sortedRides = useMemo(
        () =>
            truck.rides.sort((a: any, b: any) => {
                const timeA = a.time ? new Date(a.time).getTime() : 0;
                const timeB = b.time ? new Date(b.time).getTime() : 0;
                return timeA - timeB;
            }),
        [truck.rides]
    );

    const truckTotalDeparture = truck.rides.reduce((acc: number, r: any) => acc + (r.departureTonnage || 0), 0);
    const truckTotalRidesAppro = truck.rides.filter((r: any) => r.mode === truckModeEnum.APPRO).length;
    const truckTotalArrival = truck.rides.reduce((acc: number, r: any) => acc + (r.arrivalTonnage || 0), 0);
    const truckTotalRidesEvac = truck.rides.filter((r: any) => r.mode === truckModeEnum.EVAC).length;

    const maxVisibleRides = isMobile ? 6 : 15;

    const handleToggle = () => {
        if (truck.rides.length > 0) {
            setExpanded(!expanded);
        }
    };

    return (
        <Paper elevation={2} sx={{ mb: 0.5, overflow: 'hidden', transition: 'all 0.2s', '&:hover': { boxShadow: 3 } }}>
            {isMobile ? (
                <>
                    <Box
                        onClick={handleToggle}
                        sx={{
                            p: 0.75,
                            bgcolor: 'background.default',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            cursor: truck.rides.length > 0 ? 'pointer' : 'default',
                            '&:hover': truck.rides.length > 0 ? { bgcolor: 'action. hover' } : {},
                        }}
                    >
                        {truck.rides.length > 0 && (
                            <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Icon name={expanded ? 'keyboardArrowDownSharp' : 'keyboardArrowRightSharp'} sx={{ fontSize: '1rem' }} />
                            </Box>
                        )}

                        <Box sx={{ transform: 'scale(0.85)', transformOrigin: 'left', flexShrink: 0 }}>
                            <LicensePlate number={truck.truck?.plateNumber || ''} />
                        </Box>

                        <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ fontSize: '0.8rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                            {truck.truck?.truckTypeId?.designation || ''} -{' '}
                            {!!truck.truck.truckCompanyId.isOwnCompany ? truck.truck.userId?.username || '' : truck.truck.truckCompanyId.designation}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                            <StatDisplay count={truckTotalRidesAppro} label="Appro" locale={locale} variant="h6" minWidth={50} />
                            <StatDisplay tonnage={truckTotalDeparture} label="Appro" color="#00b8c2" locale={locale} variant="h6" minWidth={70} />
                            <StatDisplay count={truckTotalRidesEvac} label="Evac" locale={locale} variant="h6" minWidth={50} />
                            <StatDisplay tonnage={truckTotalArrival} label="Evac" color="lightgreen" locale={locale} variant="h6" minWidth={70} />
                        </Box>
                    </Box>

                    <Box sx={{ p: 0.75, pt: 0, display: 'flex', gap: 0.5, alignItems: 'center', overflowX: 'auto' }}>
                        <Chip
                            label={`${truck.rides.length} ${t('ride(s)')}`}
                            color="primary"
                            size="small"
                            sx={{ fontWeight: 'bold', height: 24, minWidth: 60, fontSize: '0.75rem', flexShrink: 0 }}
                        />

                        {sortedRides.slice(0, maxVisibleRides).map((ride: any) => (
                            <RideBadge
                                key={ride._id}
                                time={ride.time ? new Date(ride.time) : undefined}
                                departureTonnage={ride.departureTonnage}
                                arrivalTonnage={ride.arrivalTonnage}
                            />
                        ))}

                        {truck.rides.length > maxVisibleRides && (
                            <Chip
                                label={`+${truck.rides.length - maxVisibleRides}`}
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggle();
                                }}
                                sx={{
                                    cursor: 'pointer',
                                    height: 24,
                                    minWidth: 40,
                                    fontSize: '0.7rem',
                                    bgcolor: 'action.hover',
                                    '&:hover': { bgcolor: 'action.selected' },
                                    flexShrink: 0,
                                }}
                            />
                        )}
                    </Box>
                </>
            ) : (
                <Box
                    onClick={handleToggle}
                    sx={{
                        p: 0.75,
                        bgcolor: 'background.default',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        cursor: truck.rides.length > 0 ? 'pointer' : 'default',
                        '&:hover': truck.rides.length > 0 ? { bgcolor: 'action. hover' } : {},
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 250, flexShrink: 0 }}>
                        {truck.rides.length > 0 ? (
                            <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Icon name={expanded ? 'keyboardArrowDownSharp' : 'keyboardArrowRightSharp'} sx={{ fontSize: '1rem' }} />
                            </Box>
                        ) : (
                            <Box sx={{ width: 24, flexShrink: 0 }} />
                        )}

                        <Box sx={{ transform: 'scale(0.85)', transformOrigin: 'left', flexShrink: 0 }}>
                            <LicensePlate number={truck.truck?.plateNumber || ''} />
                        </Box>

                        <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}
                        >
                            {truck.truck?.truckTypeId?.designation || ''} -{' '}
                            {!!truck.truck.truckCompanyId.isOwnCompany ? truck.truck.userId?.username || '' : truck.truck.truckCompanyId.designation}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flex: 1, overflowX: 'auto', minWidth: 0 }}>
                        <Chip
                            label={`${truck.rides.length} ${t('ride(s)')}`}
                            color="primary"
                            size="small"
                            sx={{ fontWeight: 'bold', height: 24, minWidth: 60, fontSize: '0.75rem', flexShrink: 0 }}
                        />

                        {sortedRides.slice(0, maxVisibleRides).map((ride: any) => (
                            <RideBadge
                                key={ride._id}
                                time={ride.time ? new Date(ride.time) : undefined}
                                departureTonnage={ride.departureTonnage}
                                arrivalTonnage={ride.arrivalTonnage}
                            />
                        ))}

                        {truck.rides.length > maxVisibleRides && (
                            <Chip
                                label={`+${truck.rides.length - maxVisibleRides}`}
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggle();
                                }}
                                sx={{
                                    cursor: 'pointer',
                                    height: 24,
                                    minWidth: 40,
                                    fontSize: '0.7rem',
                                    bgcolor: 'action.hover',
                                    '&:hover': { bgcolor: 'action.selected' },
                                    flexShrink: 0,
                                }}
                            />
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        <StatDisplay count={truckTotalRidesAppro} label="Appro" locale={locale} variant="h6" minWidth={60} />
                        <StatDisplay tonnage={truckTotalDeparture} label="Appro" color="#00b8c2" locale={locale} variant="h6" minWidth={85} />
                        <StatDisplay count={truckTotalRidesEvac} label="Evac" locale={locale} variant="h6" minWidth={60} />
                        <StatDisplay tonnage={truckTotalArrival} label="Evac" color="lightgreen" locale={locale} variant="h6" minWidth={85} />
                    </Box>
                </Box>
            )}

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box sx={{ p: { xs: 0.75, sm: 1 }, pt: 0, bgcolor: 'background.paper' }}>
                    {sortedRides.map((ride: any, index: number) => (
                        <Paper
                            key={ride._id}
                            elevation={1}
                            sx={{
                                p: 0.75,
                                mb: 0.5,
                                transition: 'all 0.2s',
                                '&:hover': { boxShadow: 2, transform: 'translateX(4px)' },
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                            }}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                                <Chip
                                    label={`#${index + 1} - ${capitalizeFirstLetter(getTruckModeLabel(ride.mode))}`}
                                    size="small"
                                    color="default"
                                    sx={{ fontWeight: 'bold', height: 22, fontSize: '0.7rem' }}
                                />
                                <Stack direction="row" spacing={1.5}>
                                    {!!ride.departureTonnage && (
                                        <Typography variant="body2" sx={{ color: '#00b8c2', fontWeight: 'bold', fontSize: '0.85rem' }}>
                                            {formatNumber(ride.departureTonnage || 0, locale, 2)} t
                                        </Typography>
                                    )}
                                    {!!ride.arrivalTonnage && (
                                        <Typography variant="body2" sx={{ color: 'lightgreen', fontWeight: 'bold', fontSize: '0.85rem' }}>
                                            {formatNumber(ride.arrivalTonnage || 0, locale, 2)} t
                                        </Typography>
                                    )}
                                </Stack>
                            </Stack>

                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                {isTruckContextEnabled(ride.mode, 'departure') && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                        {ride.departureTime ? formatTime(new Date(ride.departureTime)) : '-'}
                                        {' → '}
                                    </Typography>
                                )}
                                {ride.time && (
                                    <Chip
                                        icon={<Icon name="place" sx={{ fontSize: '0.85rem' }} />}
                                        label={formatTime(new Date(ride.time))}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        sx={{ height: 22, fontSize: '0.7rem' }}
                                    />
                                )}
                                {isTruckContextEnabled(ride.mode, 'arrival') && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                        {' → '}
                                        {ride.arrivalTime ? formatTime(new Date(ride.arrivalTime)) : '-'}
                                    </Typography>
                                )}
                                {isTruckContextEnabled(ride.mode, 'secondTimeOnSite') && (
                                    <Typography variant="caption" color="text. secondary" sx={{ fontSize: '0.7rem' }}>
                                        {' → '}
                                        {ride.secondTime ? formatTime(new Date(ride.secondTime)) : '-'}
                                    </Typography>
                                )}
                            </Stack>

                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                {isTruckContextEnabled(ride.mode, 'departure') && (
                                    <Typography variant="caption" fontWeight="medium" sx={{ fontSize: '0. 7rem' }}>
                                        {ride.departureSiteId?.name || ride.departureQuarryId?.designation || '-'}
                                        {' → '}
                                    </Typography>
                                )}

                                <Chip
                                    icon={<Icon name="place" sx={{ fontSize: '0. 85rem' }} />}
                                    label={siteName || '-'}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    sx={{ height: 22, fontSize: '0.7rem' }}
                                />
                                {isTruckContextEnabled(ride.mode, 'arrival') && (
                                    <Typography variant="caption" fontWeight="medium" sx={{ fontSize: '0.7rem' }}>
                                        {' → '}
                                        {ride.arrivalSiteId?.name || ride.arrivalQuarryId?.designation || '-'}
                                    </Typography>
                                )}
                                {isTruckContextEnabled(ride.mode, 'secondTimeOnSite') && (
                                    <Typography variant="caption" fontWeight="medium" sx={{ fontSize: '0.7rem' }}>
                                        {' → '}
                                        {siteName || '-'}
                                    </Typography>
                                )}
                            </Stack>

                            {ride.transfers && ride.transfers.length > 0 && (
                                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                                    {ride.transfers.map((transfer: any) => (
                                        <Stack
                                            key={transfer._id}
                                            spacing={0.25}
                                            sx={{ bgcolor: getMaterialStateColor(transfer.state), p: 0.5, borderRadius: 0.75 }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color={getMaterialStateColor(transfer.state)}
                                                sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}
                                            >
                                                {transfer.machineId?.designation ||
                                                    transfer.toolId?.designation ||
                                                    `${transfer.rentalName} ${transfer.rentalCompanyId?.designation}` ||
                                                    '-'}
                                            </Typography>
                                            <Typography variant="caption" color={'black'} sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}>
                                                {transfer.siteId?.name || transfer.quarryId?.designation || '-'}
                                            </Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                        </Paper>
                    ))}
                </Box>
            </Collapse>
        </Paper>
    );
};

const TrucksLiveSupervision: React.FC<TrucksLiveSupervisionProps> = ({ date, onlyMySites = false }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { data: dataTrucksSupervision, isLoading: isLoadingTrucksSupervision } = useSitesTrucksSupervisionQuery({ date: date.toISOString() });

    const trucksSupervision = useMemo(() => {
        return (
            dataTrucksSupervision
                ?.filter((value) => !onlyMySites || [value.site.conducId?._id, value.site.assistantConducId?._id].includes(user?._id))
                .sort((a, b) => {
                    const minDisplayOrderA = Math.min(
                        ...a.trucks.map((truck) => truck.truck?.truckTypeId?.displayOrder ?? Infinity).filter((order) => order !== Infinity)
                    );
                    const minDisplayOrderB = Math.min(
                        ...b.trucks.map((truck) => truck.truck?.truckTypeId?.displayOrder ?? Infinity).filter((order) => order !== Infinity)
                    );

                    if (minDisplayOrderA === Infinity) return 1;
                    if (minDisplayOrderB === Infinity) return -1;

                    if (minDisplayOrderA !== minDisplayOrderB) return minDisplayOrderA - minDisplayOrderB;
                    return a.site.name.localeCompare(b.site.name);
                }) || []
        );
    }, [dataTrucksSupervision, onlyMySites, user?._id]);

    const summary = useMemo(() => {
        if (!trucksSupervision) {
            return { totalRides: 0, totalDeparture: 0, totalArrival: 0, totalRidesAppro: 0, totalRidesEvac: 0 };
        }

        let totalRides = 0;
        let totalDeparture = 0;
        let totalRidesAppro = 0;
        let totalArrival = 0;
        let totalRidesEvac = 0;

        trucksSupervision.forEach((siteData) => {
            siteData.trucks.forEach((truck) => {
                totalRides += truck.rides.length;
                totalDeparture += truck.rides.reduce((sum, r) => sum + (r.departureTonnage || 0), 0);
                totalRidesAppro += truck.rides.filter((r) => r.mode === truckModeEnum.APPRO).length;
                totalArrival += truck.rides.reduce((sum, r) => sum + (r.arrivalTonnage || 0), 0);
                totalRidesEvac += truck.rides.filter((r) => r.mode === truckModeEnum.EVAC).length;
            });
        });

        return { totalRides, totalDeparture, totalArrival, totalRidesAppro, totalRidesEvac };
    }, [trucksSupervision]);

    const chartOptions = useMemo((): AgChartOptions | null => {
        if (!trucksSupervision || trucksSupervision.length === 0) {
            return null;
        }

        const chartData: any[] = [];

        trucksSupervision.forEach((siteData) => {
            const totalDeparture = siteData.trucks.reduce(
                (acc, truck) => acc + truck.rides.reduce((sum, r) => sum + (r.departureTonnage || 0), 0),
                0
            );
            const totalArrival = siteData.trucks.reduce((acc, truck) => acc + truck.rides.reduce((sum, r) => sum + (r.arrivalTonnage || 0), 0), 0);

            chartData.push({
                site: siteData.site.name,
                appro: totalDeparture,
                evac: totalArrival,
            });
        });

        return {
            data: chartData,
            series: [
                { type: 'bar', xKey: 'site', yKey: 'appro', yName: 'Tonnage Appro', fill: '#00b8c2', strokeWidth: 0 },
                { type: 'bar', xKey: 'site', yKey: 'evac', yName: 'Tonnage Évac', fill: 'lightgreen', strokeWidth: 0 },
            ],
            axes: [
                { type: 'category', position: 'bottom' },
                { type: 'number', position: 'left', label: { formatter: ({ value }) => `${formatNumber(value, t('locale'), 2)} t` } },
            ],
        };
    }, [trucksSupervision, t]);

    return (
        <ScrollPageContainer loading={isLoadingTrucksSupervision}>
            {chartOptions && dataTrucksSupervision && (
                <Paper sx={{ p: 2, mb: 1 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 4 }} justifyContent="space-around" flexWrap="wrap">
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                            <Box textAlign="center">
                                <Typography variant="h4" color="primary">
                                    {summary.totalRidesAppro}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Tours Appro
                                </Typography>
                            </Box>

                            <Box textAlign="center">
                                <Typography variant="h4" sx={{ color: '#00b8c2' }}>
                                    {formatNumber(summary.totalDeparture, t('locale'), 2)} t
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Tonnage Appro
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack direction="row" spacing={{ xs: 2, sm: 4 }} justifyContent="center" flexWrap="wrap">
                            <Box textAlign="center">
                                <Typography variant="h4" color="primary">
                                    {summary.totalRidesEvac}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Tours Evac
                                </Typography>
                            </Box>

                            <Box textAlign="center">
                                <Typography variant="h4" sx={{ color: 'lightgreen' }}>
                                    {formatNumber(summary.totalArrival, t('locale'), 2)} t
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Tonnage Évac
                                </Typography>
                            </Box>
                        </Stack>
                    </Stack>
                    <GenericAgChart options={chartOptions} />
                </Paper>
            )}

            {trucksSupervision.map((siteData) => {
                const siteTotalRidesAppro = siteData.trucks.reduce(
                    (acc, truck) => acc + truck.rides.filter((r) => r.mode === truckModeEnum.APPRO).length,
                    0
                );
                const siteTotalRidesEvac = siteData.trucks.reduce(
                    (acc, truck) => acc + truck.rides.filter((r) => r.mode === truckModeEnum.EVAC).length,
                    0
                );
                const siteTotalDeparture = siteData.trucks.reduce(
                    (acc, truck) => acc + truck.rides.reduce((sum, r) => sum + (r.departureTonnage || 0), 0),
                    0
                );
                const siteTotalArrival = siteData.trucks.reduce(
                    (acc, truck) => acc + truck.rides.reduce((sum, r) => sum + (r.arrivalTonnage || 0), 0),
                    0
                );

                return (
                    <SectionCard
                        key={siteData.site._id}
                        title={siteData.site.name}
                        iconTitleName="place"
                        hideTotalPrice
                        iconTitleColor={siteData.site.conducId?.color}
                        extraActions={
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={{ xs: 1, sm: 2 }}
                                alignItems={{ xs: 'flex-start', sm: 'baseline' }}
                                flexWrap="wrap"
                                pr={1}
                                sx={{ width: { xs: '100%', sm: 'auto' } }}
                            >
                                <StatDisplay
                                    count={siteTotalRidesAppro}
                                    label="Appro"
                                    locale={t('locale')}
                                    variant="h5"
                                    minWidth={isMobile ? 60 : 80}
                                />
                                <StatDisplay
                                    tonnage={siteTotalDeparture}
                                    label="Appro"
                                    color="#00b8c2"
                                    locale={t('locale')}
                                    variant="h5"
                                    minWidth={isMobile ? 90 : 120}
                                />
                                <StatDisplay
                                    count={siteTotalRidesEvac}
                                    label="Evac"
                                    locale={t('locale')}
                                    variant="h5"
                                    minWidth={isMobile ? 60 : 80}
                                />
                                <StatDisplay
                                    tonnage={siteTotalArrival}
                                    label="Evac"
                                    color="lightgreen"
                                    locale={t('locale')}
                                    variant="h5"
                                    minWidth={isMobile ? 90 : 120}
                                />
                            </Stack>
                        }
                    >
                        {siteData.trucks
                            .sort((a, b) => {
                                const displayOrderA = a.truck?.truckTypeId?.displayOrder ?? Infinity;
                                const displayOrderB = b.truck?.truckTypeId?.displayOrder ?? Infinity;
                                if (displayOrderA !== displayOrderB) return displayOrderA - displayOrderB;

                                const getEarliest = (rides: DailyTruckRide[]) => {
                                    if (!rides?.length) return Infinity;
                                    const times = rides.map((r) => (r.time ? new Date(r.time).getTime() : Infinity));
                                    return Math.min(...times);
                                };

                                const earliestA = getEarliest(a.rides);
                                const earliestB = getEarliest(b.rides);

                                if (earliestA !== earliestB) return earliestA - earliestB;

                                return a.truck?.designation.localeCompare(b.truck?.designation) || 0;
                            })
                            .map((truck) => (
                                <TruckCard key={truck.truck._id} truck={truck} siteName={siteData.site.name} locale={t('locale')} />
                            ))}
                    </SectionCard>
                );
            })}
        </ScrollPageContainer>
    );
};

export default TrucksLiveSupervision;
